import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { type FC, useId } from "react";
import { Controller, useForm } from "react-hook-form";
import { useGoalsRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { FlatRowGroup } from "web_myplann/Shared/Components/FlatRow/FlatRowGroup";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { ReminderDateCalendar } from "web_myplann/Shared/Components/ReminderDateCalendar";
import { ReminderDuplicateAlert } from "web_myplann/Shared/Components/ReminderDuplicateAlert";
import { ReminderPresetChip } from "web_myplann/Shared/Components/ReminderPresetChip";
import { useSheetBottomSpacing } from "web_myplann/Shared/Hooks/useSheetBottomSpacing";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { findSameDayReminder } from "web_myplann/Shared/Utils/findSameDayReminder";
import {
	getReminderPresets,
	REMINDER_RECURRENCE_OPTIONS,
	type ReminderDraft,
	type ReminderEntry,
} from "web_myplann/Shared/Utils/reminder";

const R = Resources.Reminder;

type Props = {
	open: boolean;
	onClose: () => void;
	expiryDate?: Date | null;
	existingReminders: ReminderEntry[];
	onOpenExisting: (reminder: ReminderEntry) => void;
	onAdd: (draft: {
		date: Date;
		recurrence: ReminderDraft["recurrence"];
	}) => void;
};

export const ReminderAddDrawer: FC<Props> = ({
	open,
	onClose,
	expiryDate,
	existingReminders,
	onOpenExisting,
	onAdd,
}) => {
	const { palette, spacingTokens } = useTheme();
	const { t } = useResource();
	const hasGoalsRight = useGoalsRightOptional();
	const bottomSpacing = useSheetBottomSpacing(2);
	const duplicateAlertId = useId();
	const { control, handleSubmit, watch, setValue, reset } =
		useForm<ReminderDraft>({
			defaultValues: { date: null, recurrence: "yearly" },
		});

	const date = watch("date");
	const presets = getReminderPresets(expiryDate ?? null);
	const duplicate =
		hasGoalsRight && date ? findSameDayReminder(existingReminders, date) : null;

	const close = () => {
		reset();
		onClose();
	};

	const openExisting = (reminder: ReminderEntry) => {
		reset();
		onOpenExisting(reminder);
	};

	const submit = handleSubmit((values) => {
		if (!values.date) return;
		onAdd({ date: values.date, recurrence: values.recurrence });
		close();
	});

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={close}
			title={t(R.add)}
			closeButton={!hasGoalsRight}
			grabber={hasGoalsRight}
		>
			<Stack
				pt={hasGoalsRight ? spacingTokens.none : 2}
				pb={bottomSpacing}
				gap={hasGoalsRight ? spacingTokens.lg : 2}
			>
				{presets.length > 0 && (
					<Stack px={1.6} gap={1}>
						<Typography variant="caption" color={palette.grey[500]}>
							{t(R.quickChoice)}
						</Typography>
						<Stack direction="row" flexWrap="wrap" gap={1}>
							{presets.map((preset) => (
								<ReminderPresetChip
									key={preset.date.toISOString()}
									label={t(preset.label)}
									active={!!date && dayjs(date).isSame(preset.date, "day")}
									onClick={() => setValue("date", preset.date)}
								/>
							))}
						</Stack>
					</Stack>
				)}

				<FlatRowGroup>
					{hasGoalsRight && (
						<Controller
							control={control}
							name="date"
							render={({ field: { value, onChange } }) => (
								<ReminderDateCalendar value={value} onSelect={onChange}>
									{duplicate && (
										<ReminderDuplicateAlert
											id={duplicateAlertId}
											duplicate={duplicate}
											onOpen={openExisting}
										/>
									)}
								</ReminderDateCalendar>
							)}
						/>
					)}
					{!hasGoalsRight && (
						<FlatRowFormDatePicker
							control={control}
							name="date"
							label={t(R.date)}
							views={[DateView.Year, DateView.Month, DateView.Day]}
							displayFormat={DateFormat.DisplaySpaced}
							placeholder={t(R.selectDate)}
						/>
					)}
					<FlatRowFormSelect
						control={control}
						name="recurrence"
						label={t(R.recurrence)}
						options={REMINDER_RECURRENCE_OPTIONS.map((option) => ({
							value: option.value,
							label: t(option.label),
						}))}
						isLast
					/>
				</FlatRowGroup>

				<Box px={1.6}>
					<Button
						variant="primary"
						fullWidth
						disabled={!date}
						aria-describedby={duplicate ? duplicateAlertId : undefined}
						onClick={submit}
					>
						{t(R.add)}
					</Button>
				</Box>
			</Stack>
		</MyPlannDrawer>
	);
};
